'use client';

import { Button, CertificationBadge, Divider } from '@common/components';
import { SvgInfo } from '@common/icon';
import { cn } from '@core/utils';

import { TERM_CATEGORY } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import Instagram from '@/assets/svgs/instagram.svg';
import NaverBlog from '@/assets/svgs/naver_blog.svg';
import Youtube from '@/assets/svgs/youtube.svg';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

interface MainFooterProps {
  variant?: 'default' | 'donation';
}

export const MainFooter = ({ variant = 'default' }: MainFooterProps) => {
  const appVersion = useDeviceInfoStore((state) => state.appVersion);
  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  let devCount = 0;

  const router = useCustomRouter();

  const isDonation = variant === 'donation';

  return (
    <footer
      className={cn(
        'px-20 py-32 font-400 text-Gray-700 typo-body4',
        isDonation ? 'bg-Gray-50' : 'bg-Gray-white',
      )}
    >
      <div className="flex items-start justify-between pb-24">
        <ul className="flex flex-col gap-10 font-600 typo-body4">
          <li>
            <a
              href="https://forms.gle/gZjgg76YpXcWgw2p9"
              rel="noreferrer"
              target="_blank"
              className="text-Gray-900 no-underline"
            >
              제휴 및 입점 문의
            </a>
          </li>
        </ul>
      </div>
      <ul className="mb-20 flex flex-col flex-wrap items-start gap-2 p-0 text-Gray-600">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <li
          className="whitespace-nowrap break-keep flex-center typo-caption1"
          onClick={() => {
            devCount++;
            if (devCount === 10) {
              router.push(ROUTES.TEST.pathname);
            }
          }}
        >
          <button type="button">주식회사 웰로</button>
          <div className="mx-8">
            <Divider variant="vertical" className="h-10 w-1 bg-Gray-300" />
          </div>
          대표 : 김유리안나
        </li>
        <li className="pointer-events-none whitespace-nowrap break-keep flex-center typo-caption1">
          <span className="mr-4">사업자등록번호 :</span> 705-86-02126
        </li>
        <li className="whitespace-nowrap break-keep flex-center typo-caption1">
          <address className="not-italic">
            서울특별시 관악구 남부순환로 1933, 7-8층 JW타워
          </address>
        </li>
        <li className="whitespace-nowrap break-keep flex-center typo-caption1">
          <a className="text-Gray-700 underline" href="mailto:cs@wello.info">
            cs@wello.info
          </a>
          <div className="mx-8">
            <Divider variant="vertical" className="h-10 w-1 bg-Gray-300" />
          </div>
          <a href="tel:1833-9363" className="text-Gray-700">
            1833-9363
          </a>
        </li>
      </ul>
      <ul className="m-0 flex items-center justify-evenly gap-10 p-0">
        <li>
          <a
            href="https://www.ftc.go.kr/bizCommPop.do?wrkr_no=7058602126"
            rel="noreferrer"
            target="_blank"
            className="cursor-pointer whitespace-nowrap break-keep border-none font-600 text-Gray-700 no-underline typo-caption1"
          >
            사업자정보확인
          </a>
        </li>
        <li>
          <Divider variant="vertical" className="h-10 w-1 bg-Gray-300" />
        </li>
        <li>
          <button
            type="button"
            onClick={() => setTermsDrawerType(TERM_CATEGORY.SERVICE_TERMS)}
            className="cursor-pointer whitespace-nowrap break-keep border-none font-600 text-Gray-700 typo-caption1"
          >
            이용약관
          </button>
        </li>
        <li>
          <Divider variant="vertical" className="h-10 w-1 bg-Gray-300" />
        </li>
        <li>
          <a
            href="/terms/privacy_policy"
            onClick={(e) => {
              e.preventDefault();
              setTermsDrawerType(TERM_CATEGORY.PRIVACY_POLICY);
            }}
            className={cn(
              'cursor-pointer whitespace-nowrap break-keep border-none font-600 text-Gray-700 underline typo-caption1',
              isDonation && '!text-B-500',
            )}
          >
            개인정보처리방침
          </a>
        </li>
        <li>
          <Divider variant="vertical" className="h-10 w-1 bg-Gray-300" />
        </li>
        <li>
          <button
            type="button"
            onClick={() => setTermsDrawerType(TERM_CATEGORY.MARKETING_TERMS)}
            className="cursor-pointer whitespace-nowrap break-keep border-none font-600 text-Gray-700 typo-caption1"
          >
            마케팅 이용동의
          </button>
        </li>
      </ul>
      <article className="mt-14 typo-caption2">
        <section className="mb-8">
          (주)웰로에서 제공하는 정보는 신뢰할 만한 자료 및 정보로부터 얻어진
          것이나 그 정확성이나 완전성을 보장할 수 없으며, 시간이 경과함에 따라
          변경될 수 있습니다. 따라서 정보의 오류, 누락에 대하여 (주)웰로 또는
          (주)웰로에 자료를 제공하는 기관에서는 그 결과에 대해 법적인 책임을
          지지 않습니다. (주)웰로가 소유/운영/관리하는 웹사이트 및 앱의 이벤트
          정보 및 모든 정보 UI, 콘텐츠 등에 대한 무단 복제, 배포, 발송 또는
          전송, 스크래핑 등의 행위는 관련 법령에 의하여 엄격히 금지 됩니다.
        </section>
        <Button
          asChild
          className={cn(
            'w-full rounded-xxsmall font-600 typo-caption2',
            isDonation && 'bg-Gray-white',
          )}
          dimensions="H26"
          theme="sub-line"
        >
          <CustomLink href={ROUTES.CONTENT_COMPLIANCE_INFORMATION.pathname}>
            콘텐츠산업 진흥법에 따른 표시
          </CustomLink>
        </Button>
        <p className="mt-14 flex items-center gap-4 font-400 text-Gray-600 typo-caption2">
          <SvgInfo className="size-12" />
          일부 콘텐츠는 AI를 활용하여 제작되었습니다.
        </p>
      </article>
      <nav
        aria-label="소셜 미디어"
        className="flex justify-center gap-26 py-30"
      >
        <a
          aria-label="인스타그램에서 웰로 팔로우하기"
          href="https://www.instagram.com/wello.info"
          rel="noreferrer"
          target="_blank"
          className="flex items-center text-Gray-600 no-underline"
        >
          <Instagram className="size-38" />
        </a>
        <a
          aria-label="유튜브에서 웰로 구독하기"
          href="https://www.youtube.com/@wello_info"
          rel="noreferrer"
          target="_blank"
          className="flex items-center text-Gray-600 no-underline"
        >
          <Youtube className="size-38" />
        </a>
        <a
          aria-label="네이버 블로그에서 웰로 방문하기"
          href="https://blog.naver.com/wello_official"
          rel="noreferrer"
          target="_blank"
          className="flex items-center text-Gray-600 no-underline"
        >
          <NaverBlog className="size-38" />
        </a>
      </nav>

      <strong className="flex flex-col items-center font-400 text-Gray-900 typo-caption1">
        ⓒ Wello Inc.
        <div className="text-Gray-600">{appVersion}</div>
      </strong>

      <div className="my-16 h-1 w-full bg-Gray-300" />

      <CertificationBadge
        className="flex-col items-start"
        size={40}
        isHideCSAP
      />
    </footer>
  );
};
